import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';
import { motion } from 'framer-motion';

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date()?.getFullYear();

  const footerSections = [
    {
      title: "Training",
      links: [
        { label: "Live Webinars", path: "/webinars/live", icon: "Video" },
        { label: 'On-Demand Courses', path: '/webinars/recorded', icon: "Play" },
        { label: "Enterprise Solutions", path: "/webinars/live", icon: "Award" },
        { label: "Get Started", path: "/register", icon: "UserPlus" },
      ]
    },
    {
      title: "Company",
      links: [
        { label: "About Us", path: "/about", icon: "Info" },
        { label: "Contact", path: "/contact", icon: "MessageCircle" },
      ]
    },
    {
      title: "Legal",
      links: [
        { label: "Terms & Conditions", path: "/terms-conditions" },
        { label: "Privacy Policy", path: "/privacy-policy" },
        { label: "Refund Policy", path: "/refund-cancellation-policy" },
        { label: "Shipping Policy", path: "/shipping-return-policy" }
      ]
    }
  ];

  const handleLinkClick = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleSocialClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <footer className="bg-gradient-to-br from-green-900 via-emerald-900 to-green-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(16, 185, 129, 0.3) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Animated floating elements */}
      <motion.div 
        className="absolute top-20 left-10 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl"
        animate={{ 
          x: [0, 20, 0],
          y: [0, -20, 0]
        }}
        transition={{ 
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div 
        className="absolute bottom-20 right-10 w-48 h-48 bg-green-500/10 rounded-full blur-3xl"
        animate={{ 
          x: [0, -20, 0],
          y: [0, 20, 0]
        }}
        transition={{ 
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-12 sm:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Brand Section */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center space-x-3 mb-6">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg blur opacity-30"></div>
                  <img 
                    src="/assets/logo2.jpeg" 
                    alt="ComplianceTrained Logo" 
                    className="h-10 w-auto object-contain relative z-10"
                  />
                </motion.div>
                <motion.span 
                  className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent"
                  animate={{ 
                    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                  }}
                  transition={{ 
                    duration: 5,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  style={{ backgroundSize: '200% 100%' }}
                >
                  WorkForceSkilled
                </motion.span>
              </div>
              <p className="text-sm sm:text-base text-emerald-100 mb-6 leading-relaxed">
                Your trusted partner for expert compliance training solutions. Stay ahead of regulatory requirements with our comprehensive webinars and certification programs.
              </p>
            </motion.div>

            {/* Footer Links */}
            {footerSections?.map((section, sectionIndex) => (
              <motion.div 
                key={section?.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: sectionIndex * 0.1 }}
              >
                <h4 className="text-base sm:text-lg font-bold mb-4 text-green-300">{section?.title}</h4>
                <ul className="space-y-3">
                  {section?.links?.map((link, linkIndex) => (
                    <motion.li 
                      key={link?.label}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: (sectionIndex * 0.1) + (linkIndex * 0.05) }}
                    >
                      <button
                        onClick={() => handleLinkClick(link?.path)}
                        className="text-sm text-emerald-100 hover:text-green-300 transition-all duration-200 focus-ring rounded px-1 py-1 text-left flex items-center gap-2 group w-full hover:translate-x-1"
                      >
                        {link.icon && (
                          <motion.div
                            whileHover={{ rotate: 10 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Icon name={link.icon} size={14} className="text-emerald-400 group-hover:text-green-300 transition-colors" />
                          </motion.div>
                        )}
                        <span>{link?.label}</span>
                        <motion.div
                          className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                          initial={{ x: -5 }}
                          animate={{ x: 0 }}
                        >
                          <Icon name="ChevronRight" size={12} className="text-green-300" />
                        </motion.div>
                      </button>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="border-t border-emerald-800/50 py-8 sm:py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <motion.div 
              className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-br from-emerald-900/40 to-green-900/40 border border-emerald-700/50 hover:border-green-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-900/20"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <motion.div 
                className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-lg"
                whileHover={{ rotate: 10 }}
              >
                <Icon name="Mail" size={20} className="text-white" />
              </motion.div>
              <div>
                <div className="text-sm font-semibold text-emerald-200 mb-1">Email Support</div>
                <a href="mailto:support@compliancetrained.com" className="text-sm text-green-300 hover:text-green-200 transition-colors break-all inline-flex items-center gap-1 group">
                  support@compliancetrained.com
                  <motion.span
                    initial={{ x: -5, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    className="group-hover:opacity-100 opacity-0 transition-opacity"
                  >
                    ↗
                  </motion.span>
                </a>
              </div>
            </motion.div>
            
            <motion.div 
              className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-br from-green-900/40 to-emerald-900/40 border border-emerald-700/50 hover:border-green-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-900/20"
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <motion.div 
                className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0 shadow-lg"
                whileHover={{ rotate: -10 }}
              >
                <Icon name="MapPin" size={20} className="text-white" />
              </motion.div>
              <div>
                <div className="text-sm font-semibold text-emerald-200 mb-1">Our Location</div>
                <div className="text-sm text-emerald-200 leading-relaxed">
                  2/24, East Street, Thirumazhapadi(PO)<br />
                  Ariyalur (DT), Tamilnadu-621851<br />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Certifications/Trust Badges */}
        <motion.div 
          className="border-t border-emerald-800/50 py-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
            {[
              { icon: "Shield", text: "SHRM Approved", color: "text-emerald-400" },
              { icon: "Award", text: "HRCI Certified", color: "text-green-400" },
              { icon: "CheckCircle", text: "ISO 9001 Compliant", color: "text-emerald-400" }
            ].map((badge, index) => (
              <motion.div 
                key={badge.text}
                className="flex items-center gap-2 text-emerald-200 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-900/30 to-green-900/30 border border-emerald-800/50"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(16, 185, 129, 0.2)" }}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <motion.div
                  animate={{ rotate: [0, 5, 0] }}
                  transition={{ duration: 3, repeat: Infinity, delay: index * 0.5 }}
                >
                  <Icon name={badge.icon} size={20} className={`${badge.color}`} />
                </motion.div>
                <span className="text-sm font-medium">{badge.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom Footer */}
        <motion.div 
          className="border-t border-emerald-800/50 py-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <div className="text-sm text-emerald-300 text-center md:text-left">
              © {currentYear} MRTrixx Services LLP. All rights reserved. | Empowering businesses through expert compliance training.
            </div>
            <motion.div 
              className="flex items-center gap-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-xs text-emerald-400 hover:text-green-300 transition-colors flex items-center gap-1 group"
              >
                <motion.div
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Icon name="ArrowUp" size={12} />
                </motion.div>
                Back to Top
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-green-500/10 via-emerald-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-emerald-500/10 via-green-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />
    </footer>
  );
};

export default Footer;